import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { OktaService } from './okta.service';
import { environment } from '../../../environments/environment';
import { HcToastOptions, HcToasterService } from '@healthcatalyst/cashmere';

export interface StatusElement {
    'statusID': number,
    'name': string,
    'description': string
}

@Injectable()
export class StatusService {
    statusList: BehaviorSubject<StatusElement[] | null> = new BehaviorSubject<StatusElement[] | null>(null);
    statusKey: BehaviorSubject<{ [key: string]: string } | null> = new BehaviorSubject<{ [key: string]: string } | null>(null);
    loadFailed = false;

    constructor(private http: HttpClient, private oktaService: OktaService, private toasterService: HcToasterService) { };

    get statusListUpdated(): Observable<StatusElement[] | null> {
        return this.statusList.asObservable();
    }

    get statusKeyUpdated(): Observable<{ [key: string]: string } | null> {
        return this.statusKey.asObservable();
    }

    createKey(productLineList: StatusElement[]) {
        let keyObj: { [key: string]: string } = {};

        productLineList.forEach(element => {
            keyObj[element.statusID.toString()] = element.name;
        });

        return keyObj
    }

    refresh() {
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        this.http.get<any>(environment.azure.url + '/statuses', options)
            .subscribe({
                next: data => {
                    this.statusList.next(data);
                    this.statusKey.next(this.createKey(data));
                    this.loadFailed = false;
                },
                error: msg => {
                    this.loadFailed = true;
                    this.statusList.next(null);
                    console.log(msg);
                }
            });
    }

    add(target: StatusElement) {
        // Write target to database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

       
        const postSub = this.http.post<any>(environment.azure.url + '/admin.statuses.add',
            {
                name: target.name,
                description: target.description
            },
            options
        ).subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'Status Added',
                    body: target.name + " successfully added.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Add Failed',
                    body: "Unable to add status. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log(msg);
                postSub.unsubscribe();
            }
        });
    }

    update(target: StatusElement) {
        // Update target in databases
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        const postSub = this.http.post<any>(environment.azure.url + '/admin.statuses.update',
            {
                statusID: target.statusID,
                name: target.name,
                description: target.description
            },
            options
        ).subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'Status Updated',
                    body: target.name + " successfully updated.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Update Failed',
                    body: "Unable to update status. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log(msg);
                postSub.unsubscribe();
            }
        });
    }

    remove(target: StatusElement) {
        // Remove target from database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };
        const postSub = this.http.post<any>(environment.azure.url + '/admin.statuses.remove',
            {
                statusID: target.statusID
            },
            options
        ).subscribe({
            next: () => {
                this.refresh();
                const options: HcToastOptions = {
                    header: 'Status Removed',
                    body: target.name + " successfully removed.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Remove Failed',
                    body: "Unable to remove status. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log(msg);
                postSub.unsubscribe();
            }
        });
    }
}